<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { fetchData } from '../../fetchData';
import FinancialEducationItem from './FinancialEducationItem.vue';
import FinancialEducationSkeleton from './FinancialEducationSkeleton.vue';

const breakingNews = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const error = ref(null);
const page = ref(1);
const limit = 15;
const hasMore = ref(true);
const scrollContainer = ref(null);

const fetchNews = async () => {
    if (loadingMore.value || !hasMore.value) {
        return;
    }

    if (page.value > 1) {
        loadingMore.value = true;
    }

    error.value = null;

    try {
        const { result = {} } = await fetchData(`/api/v1/posts?limit=${limit}&page=${page.value}&post_categories[]=Finanzbildung&sort[by]=created_at&sort[order]=desc`);

        if (result.meta.current_page < result.meta.last_page) {
            breakingNews.value.push(...result.data);
            page.value++;

            if (result.data.length < limit) {
                hasMore.value = false;
            }
        } else {
            hasMore.value = false;
        }
    } catch (e) {
        error.value = 'Failed to load breaking news: ' + e.message;
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const handleScroll = () => {
    const el = scrollContainer.value;

    if (el) {
        const buffer = 100;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - buffer;

        if (isAtBottom) {
            fetchNews();
        }
    }
};

onMounted(() => {
    fetchNews();
    const el = scrollContainer.value;

    if (el) {
        el.addEventListener('scroll', handleScroll);
    }
});

onUnmounted(() => {
    const el = scrollContainer.value;

    if (el) {
        el.removeEventListener('scroll', handleScroll);
    }
});
</script>

<template>
    <div ref="scrollContainer" class="mt-2 overflow-y-auto h-full" @scroll="handleScroll">
        <div v-if="loading" class="space-y-2">
            <FinancialEducationSkeleton v-for="n in 3" :key="`sk-load-${n}`" />
        </div>
        <div v-else-if="error" class="text-center text-red-500 py-4">
            {{ error }}
        </div>
        <div v-else-if="breakingNews.length === 0" class="text-center text-gray-500 py-4">
            {{ $t('no_breaking_news') }}
        </div>
        <div v-else class="space-y-2">
            <FinancialEducationItem v-for="post in breakingNews" :key="post.id" :post="post" />
        </div>
        <div v-if="loadingMore" class="space-y-2 mt-2">
            <FinancialEducationSkeleton key="sk-more" />
        </div>
    </div>
</template>
